import { Component, OnInit, OnDestroy, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DashboardService } from '../../core/services/dashboard.service';
import {
  OrganizationActivity,
  getActivityIcon,
  getResourceRoute
} from '../../core/models/organization-activity.model';

/**
 * Activity Page Component
 * Full page view of all organization activities with pagination
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Component({
  selector: 'app-activity',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit, OnDestroy {
  private dashboardService = inject(DashboardService);
  private router = inject(Router);

  // Loading state for initial load
  isInitializing = signal(true);

  // Filter state: show only unseen activities
  showUnseenOnly = signal(false);

  // Activities from DashboardService (filtered based on showUnseenOnly)
  readonly allActivities = computed(() => this.dashboardService.activities$());
  readonly unseenActivities = computed(() => this.dashboardService.unseenActivities$());
  readonly activities = computed(() =>
    this.showUnseenOnly() ? this.unseenActivities() : this.allActivities()
  );
  readonly isLoadingActivities = computed(() => this.dashboardService.isLoadingActivities$());
  readonly hasMore = computed(() => this.dashboardService.hasMore$());
  readonly newActivityIds = computed(() => this.dashboardService.newActivityIds$());
  readonly unseenCount = computed(() => this.dashboardService.unseenActivityCount$());

  async ngOnInit(): Promise<void> {
    // Initialize dashboard data (which includes activities)
    await this.dashboardService.initialize();
    this.isInitializing.set(false);

    // Mark activities as seen when viewing the activity page
    await this.dashboardService.markActivitiesAsSeen();
  }

  ngOnDestroy(): void {
    // DashboardService handles its own cleanup
  }

  /**
   * Toggle filter between all activities and unseen only
   */
  toggleUnseenFilter(): void {
    this.showUnseenOnly.update(v => !v);
  }

  /**
   * Get the icon class for an activity based on its event type
   */
  getActivityIconClass(activity: OrganizationActivity): string {
    return getActivityIcon(activity.eventType);
  }

  /**
   * Get relative time string for an activity
   */
  getRelativeTime(createdAt: string): string {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return 'Just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Check if activity has a navigable resource
   */
  hasResourceLink(activity: OrganizationActivity): boolean {
    return !!activity.resourceType && !!activity.resourceId;
  }

  /**
   * Navigate to activity resource
   */
  navigateToResource(activity: OrganizationActivity): void {
    const route = getResourceRoute(activity.resourceType, activity.resourceId);
    if (route) {
      this.router.navigate([route]);
    }
  }

  /**
   * Check if activity is newly added (for animation)
   */
  isNewActivity(activityId: string): boolean {
    return this.newActivityIds().has(activityId);
  }

  /**
   * Load more activities (pagination)
   */
  async loadMore(): Promise<void> {
    await this.dashboardService.loadMoreActivities();
  }

  /**
   * Refresh activities list
   */
  async refresh(): Promise<void> {
    await this.dashboardService.refreshActivities();
  }
}
